import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCssVariable } from 'src/utils/utils';
import { client } from 'src/utils/api';
import Layout from 'src/pages/Layout/Layout';
import { useAppContext } from 'src/components/contextAPI/AppContext';
import ProductContent from '../Products/components/ProductContent';
import ProductHero from '../Products/components/ProductHero';
import CapabilityComponent from '../Home/components/capability/CapabilityComponent';
import SiteTabList from '../SiteScore/components/SiteTabList';
import EventProfileList from './EventProfileList';

const EventLanding = () => {
  const gqlClient = client;
  const navigate = useNavigate();
  const primaryColor = getCssVariable('--primary-color');
  const [productPageData, setProductPageData] = useState<any[]>([]);
  const [seoData, setSeoData] = useState<any>();
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const pathParts = location.pathname.toLowerCase().split('/').filter(Boolean);
  const isBaseRoute = pathParts.length === 1 && pathParts[0] === 'partnership';
  const slug = isBaseRoute ? 'event-landing' : pathParts[1];

  // Add context hook
  const { updateData } = useAppContext();

  useEffect(() => {
    const fetchProductPageData = async () => {
      try {
        setLoading(true);

        const productResponse = await gqlClient.GetPageSiteBySlug({
          slug: 'event-landing',
        });

        const productId = productResponse?.pageGenericCollection?.items[0]?.sys.id;

        if (!productId) {
          navigate('/not-found', { replace: true });
          return;
        }
        const [responseOne, responseTwo, responseThree] = await Promise.all([
          gqlClient.GetEventLandingOne({ id: productId }),
          gqlClient.GetEventLandingTwo({ id: productId }),
          gqlClient.GetEventLandingThree({ id: productId, infoLimit: 5 }),
        ]);

        const componentsOne = responseOne?.pageGeneric?.componentsCollection?.items || [];
        const componentsTwo = responseTwo?.pageGeneric?.componentsCollection?.items || [];
        const componentsThree = responseThree?.pageGeneric?.componentsCollection?.items || [];
        const cleanedData = [...componentsOne, ...componentsTwo, ...componentsThree].filter(
          item => item && item.sys?.id && Object.keys(item).length > 2,
        );

        setProductPageData(cleanedData);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching Product data:', error);
        setLoading(false);
      }
    };

    fetchProductPageData();
  }, [slug]);

  useEffect(() => {
    if (productPageData) {
      const seoComponent = productPageData?.find((item: any) => item.__typename === 'SeoFields');
      if (seoComponent) setSeoData(seoComponent);
    }
  }, [productPageData]);
  useEffect(() => {
    if (productPageData && productPageData.length > 0) {
      const contact = productPageData.filter(
        comp => comp.__typename === "ImageAndText" && comp.variant == null
      );

      // Get the first contact item (assuming there's only one)
      const contactData = contact[0];

      if (contactData) {
        const contextData = {
          title: contactData.title || "",
          buttonLink: {
            buttonImage: contactData.buttonLink?.buttonImage || null,
            buttonLink: contactData.buttonLink?.buttonLink || null,
            buttonText: contactData.buttonLink?.buttonText || "Meet with Gradial",
            buttonType: contactData.buttonLink?.buttonType || null
          }
        };

        updateData(contextData);

      }
    }
  }, [productPageData]);
  // New useEffect to send contact data to context
  useEffect(() => {
    if (productPageData && productPageData.length > 0) {
      const contact = productPageData.filter(
        comp => comp.__typename === 'ImageAndText' && comp.variant == null,
      );

      // Get the first contact item (assuming there's only one)
      const contactData = contact[0];

      if (contactData) {
        const contextData = {
          title: contactData.title || '',
          buttonLink: {
            buttonImage: contactData.buttonLink?.buttonImage || null,
            buttonLink: contactData.buttonLink?.buttonLink || null,
            buttonText: contactData.buttonLink?.buttonText || 'Meet with Gradial',
            buttonType: contactData.buttonLink?.buttonType || null,
          },
        };

        updateData(contextData);
      }
    }
  }, [productPageData]);

  const renderComponent = (component: any) => {
    const componentMap = {
      ImageAndText: {
        TextOnly: ProductHero,
        TextAndImage: props => (
          <div>
            <ProductContent {...props} className="promise-event-form" />
          </div>
        ),
      },
      ProfilleData: EventProfileList,
      VerticalTabList: props => (
        <div>
          <CapabilityComponent {...props} className="event-verticaltab" />
        </div>
      ),
        TabList: SiteTabList,
    };

    const contentTypeId = component.__typename;
    const variant = component.variant;

    // Handle components with variants
    if (contentTypeId === 'ImageAndText') {
      const Component = componentMap[contentTypeId]?.[variant];

      if (Component) {
        return <Component key={component.sys.id} content={component} />;
      }
      console.warn(`Unmapped variant: ${variant} for contentTypeId: ${contentTypeId}`);
      return null;
    } else {
      // Handle all other components
      const Component = componentMap[contentTypeId];
      console.log('Component data', Component, component);
      if (Component) {
        return <Component key={component.sys.id} content={component} />;
      }
      console.warn(`Unmapped component: ${contentTypeId}`);
      return null;
    }
  };

  // This function reorganizes components in the desired order
  const getReorderedComponents = () => {
    if (!productPageData || productPageData.length === 0) return [];

    const imageAndText = productPageData.filter(comp => comp.__typename === 'ImageAndText');
    const ProfilleData = productPageData.filter(comp => comp.__typename === 'ProfilleData');
    const VerticalTabList = productPageData.filter(comp => comp.__typename === 'VerticalTabList');
    const tabList = productPageData.filter(comp => comp.__typename === 'TabList');

    // Other components that don't match the specific criteria above
    const otherComponents = productPageData.filter(
      comp =>
        !(comp.__typename === 'ImageAndText') &&
        !(comp.__typename === 'ProfilleData') &&
        !(comp.__typename === 'VerticalTabList') &&
        !(comp.__typename === 'TabList'),
    );
    return [
      ...imageAndText, // TextAndImage
      ...VerticalTabList, // TabList
      ...ProfilleData, // ProfilleData
      ...tabList, // TabList
      ...otherComponents,
    ];
  };

  if (loading) {
    return <Layout loading={loading} />;
  }

  return (
    <Layout
      style={{ color: '#fff' }}
      seoData={seoData}
      hamburgerStyle={{ backgroundColor: primaryColor }}
      saleClassName="btn-gradient-bdr"
      loading={loading}
      headerStyle={{ position: 'relative' }}
    >
      <div className="event-page">
        {getReorderedComponents().map(component => renderComponent(component))}
      </div>
    </Layout>
  );
};

export default EventLanding;
